import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { usePlan } from "../context/PlanContext";
import { toast } from "react-toastify";
import PaymentUnavailableDialog from "./PaymentUnavailableDialog";
import "../Css/UpgradeModal.css";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
}

export default function UpgradeModal() {
  const {
    upgradeModal,
    closeUpgradeModal,
    billing,
    refreshBilling,
  } = usePlan();
  const [paying, setPaying] = useState(false);
  const [paymentUnavailable, setPaymentUnavailable] = useState({
    open: false,
    actionLabel: "online payment",
  });

  const payload = upgradeModal.payload || {};
  const upgrade = payload.upgrade || billing?.upgrade;
  const adminEmail =
    sessionStorage.getItem("adminEmail") ||
    sessionStorage.getItem("userEmail");
  const isBlocked = !!payload.blocked;

  useEffect(() => {
    if (upgradeModal.open && !billing) refreshBilling();
  }, [upgradeModal.open, billing, refreshBilling]);

  const advancePlan = (billing?.plans || []).find(
    (p) => String(p.planName).toLowerCase() === "advance",
  );

  const showPaymentUnavailable = (actionLabel) => {
    setPaymentUnavailable({ open: true, actionLabel });
  };

  const startUpgradePayment = async () => {
    if (upgrade?.contactSales) {
      window.open(
        "https://zordigo.com/pricing.html",
        "_blank",
        "noopener,noreferrer",
      );
      return;
    }
    if (!advancePlan) {
      toast.error("Advance plan not available. Please contact support.");
      return;
    }
    if (!billing?.razorpayConfigured) {
      showPaymentUnavailable("plan upgrade payment");
      return;
    }

    setPaying(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Failed to load Razorpay");

      const res = await fetch("/api/billing/upgrade/create-order", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          adminEmail,
          toPlanNumber: advancePlan.planNumber,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 503 || /not configured/i.test(data.message || "")) {
          showPaymentUnavailable("plan upgrade payment");
          return;
        }
        throw new Error(data.message || "Could not start payment");
      }

      if (data.applied) {
        toast.success(`Upgraded to ${data.plan?.planName || "new plan"}`);
        await refreshBilling();
        closeUpgradeModal();
        return;
      }

      const rzp = new window.Razorpay({
        key: data.keyId,
        amount: data.amount,
        currency: data.currency || "INR",
        name: "Zordigo",
        description: `Upgrade to ${advancePlan.planName}`,
        order_id: data.razorpayOrderId,
        handler: async (response) => {
          const verify = await fetch("/api/billing/verify", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ...response,
              type: "plan_upgrade",
              adminEmail,
              toPlanNumber: advancePlan.planNumber,
            }),
          });
          const v = await verify.json();
          if (!verify.ok) {
            toast.error(v.message || "Payment verification failed");
            return;
          }
          toast.success(`Upgraded to ${advancePlan.planName}`);
          await refreshBilling();
          closeUpgradeModal();
        },
      });
      rzp.open();
    } catch (err) {
      if (/not configured|503/i.test(err.message || "")) {
        showPaymentUnavailable("plan upgrade payment");
      } else {
        toast.error(err.message || "Payment failed");
      }
    } finally {
      setPaying(false);
    }
  };

  const payInvoice = async () => {
    if (!billing?.razorpayConfigured) {
      showPaymentUnavailable("invoice payment");
      return;
    }
    const invoice =
      (payload.invoiceId &&
        billing?.invoices?.find((i) => String(i._id) === String(payload.invoiceId))) ||
      billing?.invoices?.find(
        (i) => i.status === "due" || i.status === "overdue" || i.status === "unpaid",
      );
    if (!invoice?._id) {
      toast.error("No unpaid invoice found. Open Billing to pay.");
      return;
    }

    setPaying(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("Failed to load Razorpay");

      const res = await fetch(
        `/api/billing/invoice/${invoice._id}/create-order`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ adminEmail }),
        },
      );
      const data = await res.json();
      if (!res.ok) {
        if (res.status === 503 || /not configured/i.test(data.message || "")) {
          showPaymentUnavailable("invoice payment");
          return;
        }
        throw new Error(data.message || "Could not start payment");
      }

      const rzp = new window.Razorpay({
        key: data.keyId,
        amount: data.amount,
        currency: data.currency || "INR",
        name: "Zordigo",
        description: `Invoice ${invoice.month || ""}`.trim(),
        order_id: data.razorpayOrderId,
        handler: async (response) => {
          const verify = await fetch("/api/billing/verify", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ...response,
              type: "overage_invoice",
              adminEmail,
              invoiceId: invoice._id,
            }),
          });
          const v = await verify.json();
          if (!verify.ok) {
            toast.error(v.message || "Payment verification failed");
            return;
          }
          toast.success("Invoice paid — account unlocked");
          await refreshBilling();
          closeUpgradeModal();
        },
      });
      rzp.open();
    } catch (err) {
      if (/not configured|503|Failed to load Razorpay/i.test(err.message || "")) {
        showPaymentUnavailable("invoice payment");
      } else {
        toast.error(err.message || "Payment failed");
      }
    } finally {
      setPaying(false);
    }
  };

  const primaryLabel = isBlocked
    ? "Pay invoice"
    : upgrade?.contactSales
      ? "Contact sales"
      : billing?.razorpayConfigured === false
        ? "Upgrade (coming soon)"
        : `Upgrade to ${upgrade?.nextPlan || "Advance"}`;

  return (
    <>
      <Dialog
        open={!!upgradeModal.open}
        onClose={closeUpgradeModal}
        maxWidth="sm"
        fullWidth
        className="upgrade-modal-root"
        BackdropProps={{ className: "upgrade-modal-backdrop" }}
        PaperProps={{ className: "upgrade-modal-paper" }}
      >
        <div className="upgrade-modal-header">
          <div className="upgrade-modal-header-main">
            <div className={`upgrade-modal-icon${isBlocked ? " locked" : ""}`}>
              {isBlocked ? "!" : "↑"}
            </div>
            <div>
              <h2 className="upgrade-modal-heading">
                {isBlocked ? "Account locked" : "Upgrade your plan"}
              </h2>
              <p className="upgrade-modal-subtitle">
                {isBlocked
                  ? "Pay the outstanding invoice to unlock order creation."
                  : upgrade?.nextPlan
                    ? `Unlock more capacity with ${upgrade.nextPlan}.`
                    : "Unlock more seats, orders, and features."}
              </p>
            </div>
          </div>
          <IconButton
            className="upgrade-modal-close"
            onClick={closeUpgradeModal}
            size="small"
            aria-label="Close"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <DialogContent className="upgrade-modal-body">
          {payload.message && (
            <p className="upgrade-modal-message">{payload.message}</p>
          )}

          {upgrade?.perks?.length > 0 && (
            <div className="upgrade-modal-card">
              <p className="upgrade-modal-card-title">
                {upgrade.nextPlan
                  ? `${upgrade.nextPlan} includes`
                  : "Upgrade benefits"}
              </p>
              <ul className="upgrade-modal-perks">
                {upgrade.perks.map((perk) => (
                  <li key={perk}>{perk}</li>
                ))}
              </ul>
              {upgrade.monthlyCharge != null && (
                <p className="upgrade-modal-price">
                  From ₹{upgrade.monthlyCharge} / month
                </p>
              )}
            </div>
          )}

          {billing?.usage && (
            <div className="upgrade-modal-card">
              <p className="upgrade-modal-usage">
                Orders this month:{" "}
                <strong>{billing.usage.used}</strong>
                {billing.usage.freeLimit >= 0
                  ? ` / ${billing.usage.freeLimit}`
                  : " (unlimited)"}
              </p>
            </div>
          )}

          {billing && billing.razorpayConfigured === false && (
            <p className="upgrade-modal-notice">
              Online checkout is not connected yet. You can still review plan
              benefits — use Contact / pricing to upgrade offline.
            </p>
          )}
        </DialogContent>

        <DialogActions className="upgrade-modal-actions">
          <Button className="upgrade-modal-btn-muted" onClick={closeUpgradeModal}>
            Close
          </Button>
          <Button
            className="upgrade-modal-btn-primary"
            variant="contained"
            disabled={paying}
            onClick={isBlocked ? payInvoice : startUpgradePayment}
          >
            {paying ? "Please wait…" : primaryLabel}
          </Button>
        </DialogActions>
      </Dialog>

      <PaymentUnavailableDialog
        open={paymentUnavailable.open}
        actionLabel={paymentUnavailable.actionLabel}
        onClose={() =>
          setPaymentUnavailable((s) => ({ ...s, open: false }))
        }
      />
    </>
  );
}
